import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { AdminPhaseAssessment } from '@/types/admin-trace';

function phaseStatusVariant(status: AdminPhaseAssessment['status']) {
  switch (status) {
    case 'success':
      return 'success' as const;
    case 'failed':
    case 'aborted':
      return 'destructive' as const;
    case 'pending':
      return 'warning' as const;
    case 'notReached':
      return 'outline' as const;
    case 'unknown':
    default:
      return 'secondary' as const;
  }
}

/**
 * Phasen-Leiste: Identify → PreparePayment → Capture (Spec 143 §5.3).
 * ddbStatus/cwStatus/mismatch sind strikt optional — die Basis-Response
 * traegt sie nicht, und auch mit CW-Daten nicht auf jeder Phase (Trap §8.11).
 */
export function TracePhases({ phases }: { phases: AdminPhaseAssessment[] }) {
  const { t } = useTranslation('transactions');

  if (phases.length === 0) return null;

  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="trace-phases">
      {phases.map((p) => (
        <div
          key={p.phase}
          className={cn(
            'rounded-md border p-3',
            p.mismatch ? 'border-red-300 bg-red-50' : 'border-border',
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{p.phase}</p>
            <Badge variant={phaseStatusVariant(p.status)}>
              {t(`trace.phases.status.${p.status}`, p.status)}
            </Badge>
          </div>
          {(p.ddbStatus != null || p.cwStatus != null) && (
            <p className="mt-2 text-xs text-muted-foreground">
              {p.ddbStatus != null && <>DDB: <span className="font-mono">{p.ddbStatus}</span></>}
              {p.ddbStatus != null && p.cwStatus != null && ' · '}
              {p.cwStatus != null && <>CW: <span className="font-mono">{p.cwStatus}</span></>}
            </p>
          )}
          {p.mismatch && (
            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-red-700">
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {t('trace.phases.mismatch')}
            </p>
          )}
          {p.durationMs != null && (
            <p className="mt-1 text-xs text-muted-foreground">{p.durationMs} ms</p>
          )}
        </div>
      ))}
    </div>
  );
}
